import {
    Box,
    Collapse,
    Slider,
    Tooltip,
    Typography,
    type TooltipProps,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import NumberInput from '~/components/Inputs/NumberInput';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import { MAX_SMOOTHING_LEVEL } from '~/engine/util/algorithms/sdf/GaussianSmoothing';

/**
 * Shared stage-row idioms of the operation panels (threshold
 * segmentation, SUV threshold): a CompactToggle paired with the value
 * control of the stage it enables. Extracted from the threshold-seg
 * panel's inline rows so a second panel reuses the exact rendering
 * instead of copying it — pure stateless components, all state and unit
 * conversion stay at the call site (docs/ui_specs.md: cm/cm² at the UI
 * boundary means the CALLER converts, these rows only render).
 */

const rowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
} as const;

/**
 * Which way a stage row's explanation opens where the rows stack in an
 * operation panel's column: up, and pinned left so a two-line box grows
 * over the viewport rather than back onto the operations rail.
 */
export const STAGE_ROW_TOOLTIP_PLACEMENT: TooltipProps['placement'] =
    'top-start';

/**
 * Width reserved for the unit after a stage's input. Fixed, and reserved
 * even for a unitless row, so every input in a panel shares one right
 * edge: without it a "cm³" row pushes its box further left than a "cm"
 * one, and the column reads as ragged.
 */
const UNIT_COLUMN_WIDTH = 26;

/** The numeric part of a stage row, shared by every row shape here. */
interface StageValueProps {
    /** Value in the DISPLAY unit — the caller converts (e.g. cm² ↔ mm²). */
    value: number;
    /** Called with the new display-unit value (never null — a cleared
     *  field is swallowed, so a stage keeps the last number it could
     *  commit rather than falling to an implicit zero). */
    onValueChange: (value: number) => void;
    min: number;
    /** Upper bound; unbounded when omitted. */
    max?: number;
    step: number;
    precision: number;
    snapToStep?: boolean;
    /**
     * Reports whether the field currently shows a value it can commit.
     * A consumer that must not act on the last committed number while
     * the box shows something else gates on this.
     */
    onValidityChange?: (isValid: boolean) => void;
    /** Unit rendered after the input (e.g. "cm²"); omitted for unitless
     *  counts, whose column is reserved all the same. */
    unit?: string;
    /** Test id on the input. */
    dataTestId?: string;
}

/**
 * A stage's numeric input and its reserved unit column.
 *
 * `commitOnChange` is deliberate: consumers key a preview on this value,
 * so the change has to register the moment the field shows the new
 * number. Deferring to blur left a panel presenting a preview as current
 * while the visible value no longer matched it. Cheap by contract — a
 * parameter change never fires a compute by itself.
 */
function StageValue({
    value,
    onValueChange,
    min,
    max,
    step,
    precision,
    snapToStep,
    onValidityChange,
    unit,
    dataTestId,
    disabled,
}: StageValueProps & { disabled?: boolean }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 90 }}>
                <NumberInput
                    value={value}
                    onChange={(v) => {
                        if (v !== null) {
                            onValueChange(v);
                        }
                    }}
                    min={min}
                    max={max}
                    step={step}
                    precision={precision}
                    snapToStep={snapToStep}
                    onValidityChange={onValidityChange}
                    stepOnWheel
                    commitOnChange
                    disabled={disabled}
                    fullWidth
                    data-testid={dataTestId}
                />
            </Box>
            <Typography
                variant="body2"
                sx={{ flexShrink: 0, width: UNIT_COLUMN_WIDTH }}
            >
                {unit ?? ''}
            </Typography>
        </Box>
    );
}

interface ToggleNumberRowProps extends StageValueProps {
    labelId: string;
    tooltipId: string;
    /** Interpolation values shared by the label and tooltip messages
     *  (e.g. a structure name), for rows whose text names a live object. */
    messageValues?: Record<string, string | number>;
    enabled: boolean;
    onEnabledChange: (enabled: boolean) => void;
    /** Test id on the toggle row — the click target that enables the
     *  stage, addressed separately from its value. */
    toggleDataTestId?: string;
    /** Which way the stage's explanation opens; the stacked-row placement
     *  unless given. */
    tooltipPlacement?: TooltipProps['placement'];
}

/** A toggle + numeric value row (min-size, keep-largest N). */
export function ToggleNumberRow({
    labelId,
    tooltipId,
    messageValues,
    enabled,
    onEnabledChange,
    value,
    onValueChange,
    min,
    max,
    step,
    precision,
    snapToStep,
    onValidityChange,
    unit,
    dataTestId,
    toggleDataTestId,
    tooltipPlacement = STAGE_ROW_TOOLTIP_PLACEMENT,
}: ToggleNumberRowProps) {
    return (
        <Box sx={rowSx}>
            <CompactToggle
                labelId={labelId}
                tooltipId={tooltipId}
                messageValues={messageValues}
                value={enabled}
                onChange={onEnabledChange}
                dataTestId={toggleDataTestId}
                wrapLabel
                tooltipPlacement={tooltipPlacement}
            />
            <StageValue
                value={value}
                onValueChange={onValueChange}
                min={min}
                max={max}
                step={step}
                precision={precision}
                snapToStep={snapToStep}
                onValidityChange={onValidityChange}
                unit={unit}
                dataTestId={dataTestId}
                disabled={!enabled}
            />
        </Box>
    );
}

interface ValueRowProps extends StageValueProps {
    labelId: string;
    /** Hint on the label, when the row's meaning needs one. */
    tooltipId?: string;
    /** Interpolation values shared by the label and tooltip messages. */
    messageValues?: Record<string, string | number>;
    /** Which way the label's hint opens; `left` clears the stacked rows. */
    tooltipPlacement?: TooltipProps['placement'];
}

/**
 * A labelled numeric row for a stage that is always active, its strength
 * carried by the value alone (a margin, where 0 means "no margin").
 * {@link ToggleNumberRow} without the toggle, and laid out on the same
 * grid, so a panel mixing the two reads as one column of stages rather
 * than as inputs belonging to the row above.
 */
export function ValueRow({
    labelId,
    tooltipId,
    messageValues,
    value,
    onValueChange,
    min,
    max,
    step,
    precision,
    snapToStep,
    onValidityChange,
    unit,
    dataTestId,
    tooltipPlacement = 'left',
}: ValueRowProps) {
    const label = (
        <Typography
            variant="body2"
            fontSize={11}
            noWrap
            color="rgba(255, 255, 255, 0.85)"
            sx={{ userSelect: 'none' }}
        >
            <FormattedMessage id={labelId} values={messageValues} />
        </Typography>
    );
    return (
        <Box sx={rowSx}>
            {tooltipId !== undefined ? (
                <Tooltip
                    title={
                        <FormattedMessage
                            id={tooltipId}
                            values={messageValues}
                        />
                    }
                    placement={tooltipPlacement}
                >
                    {label}
                </Tooltip>
            ) : (
                label
            )}
            <StageValue
                value={value}
                onValueChange={onValueChange}
                min={min}
                max={max}
                step={step}
                precision={precision}
                snapToStep={snapToStep}
                onValidityChange={onValidityChange}
                unit={unit}
                dataTestId={dataTestId}
            />
        </Box>
    );
}

/**
 * Quantum the smoothing-strength slider commits positions on. Visually
 * stepless (no marks, no perceptible detents), but the reachable set is
 * finite and exact, so identical settings reproduce an identical sigma
 * and hence an identical structure (see `getSigmaForStrength`).
 */
const SMOOTHING_STRENGTH_STEP = 0.1;

/** Snap a slider position onto the committed 0.1 grid. MUI already
 *  rounds to the step, but the determinism guarantee is the caller's to
 *  make, not a detail of the widget's internals. */
function quantizeSmoothingStrength(position: number): number {
    return (
        Math.round(position / SMOOTHING_STRENGTH_STEP) * SMOOTHING_STRENGTH_STEP
    );
}

/**
 * Screen-reader value of the strength slider, as a percentage of its
 * travel. The control shows NO number by design — a smoothing strength
 * has no clinically meaningful unit at the panel, and a "6.3 of 10"
 * readout invents a precision the user cannot act on — but a slider that
 * announces nothing is unusable without sight, so the position is
 * announced as a share of the range. Derived deterministically from the
 * (already quantized) position.
 */
function strengthPercentFraction(position: number): number {
    const span = MAX_SMOOTHING_LEVEL - 1;
    return Math.round(((position - 1) / span) * 100) / 100;
}

interface SmoothingToggleRowProps {
    labelId: string;
    tooltipId: string;
    enabled: boolean;
    onEnabledChange: (enabled: boolean) => void;
    /** i18n keys of the two end labels — the only value the control
     *  shows, so they are passed explicitly rather than derived from
     *  `labelId`. */
    lightLabelId: string;
    strongLabelId: string;
    /** Continuous strength position, 1..MAX_SMOOTHING_LEVEL. */
    strength: number;
    /** Called with the quantized position (0.1 grid). */
    onStrengthChange: (strength: number) => void;
    /** Which way the stage's explanation opens; the stacked-row placement
     *  unless given. The strength slider sits under the toggle, so `bottom`
     *  is the one direction this row cannot use. */
    tooltipPlacement?: TooltipProps['placement'];
}

/**
 * The Gaussian-smoothing toggle + strength slider row.
 *
 * The slider is CONTINUOUS and carries no numeric readout: its ends are
 * labelled light ⟷ strong, which is what the user actually judges the
 * result by. The scale is nonetheless the engine's level scale (1..10, a
 * geometric sigma progression), so travel is perceptually even and every
 * integer position still lands on a table sigma.
 *
 * When the stage is OFF the slider and its end labels are removed
 * entirely rather than shown disabled — a strength reading has no meaning
 * for a stage that will not run, and a greyed-out control invites the
 * user to read a value the result does not depend on. The collapse
 * animates so the rows below slide rather than jump. NOTE: the other
 * stage rows here ({@link ToggleNumberRow}) disable-but-show their input;
 * this row deliberately diverges.
 */
export function SmoothingToggleRow({
    labelId,
    tooltipId,
    enabled,
    onEnabledChange,
    lightLabelId,
    strongLabelId,
    strength,
    onStrengthChange,
    tooltipPlacement = STAGE_ROW_TOOLTIP_PLACEMENT,
}: SmoothingToggleRowProps) {
    const intl = useIntl();
    return (
        <Box>
            <CompactToggle
                labelId={labelId}
                tooltipId={tooltipId}
                value={enabled}
                onChange={onEnabledChange}
                tooltipPlacement={tooltipPlacement}
            />
            <Collapse in={enabled} unmountOnExit>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        noWrap
                        sx={{ flexShrink: 0 }}
                    >
                        {intl.formatMessage({ id: lightLabelId })}
                    </Typography>
                    <Slider
                        size="small"
                        value={strength}
                        min={1}
                        max={MAX_SMOOTHING_LEVEL}
                        step={SMOOTHING_STRENGTH_STEP}
                        onChange={(_, value) =>
                            onStrengthChange(
                                quantizeSmoothingStrength(value as number)
                            )
                        }
                        // No number, ever: the ends say what the control
                        // does, and the screen reader gets the position as
                        // a share of the range instead of a fake-precise
                        // level.
                        valueLabelDisplay="off"
                        aria-label={intl.formatMessage({ id: labelId })}
                        aria-valuetext={intl.formatNumber(
                            strengthPercentFraction(strength),
                            { style: 'percent' }
                        )}
                    />
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        noWrap
                        sx={{ flexShrink: 0 }}
                    >
                        {intl.formatMessage({ id: strongLabelId })}
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
}
